import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { xor } from 'lodash'
import { useMessagesStore } from '@/stores/messages'
import { useMediaSearchStore } from '@/stores/media_search.js'
import { useMediumStore } from '@/stores/medium.js'

export const useMediaStore = defineStore('media', {
  state: () => ({
    media: [],
    selectedMediaIds: [],
    loading: false,
    page: {
      current: 1,
      per: null,
      total: null,
      total_records: null,
      next: null,
      previous: null
    }
  }),

  getters: {
    soleSelected({ media, selectedMediaIds }) {
      if (selectedMediaIds.length !== 1) return null

      return media.find((medium) => medium.id === selectedMediaIds[0])
    },

    selected({ media, selectedMediaIds }) {
      return media.filter((medium) => selectedMediaIds.includes(medium.id))
    }
  },

  actions: {
    async search(options) {
      const mediaSearchStore = useMediaSearchStore()

      if (options?.page) this.page.current = options.page
      const { current: page } = this.page

      this.loading = true
      return httpClient.get('/api/internal/media', {
        params: {
          ...mediaSearchStore.forQueryParams,
          page
        }
      }).then(({ data: { meta, media } }) => {
        this.clearSelected()
        this.media = media
        this.page = meta.page
        this.loading = false
      })
    },

    toggle(id) {
      this.selectedMediaIds = xor(this.selectedMediaIds, [id])

      const mediumStore = useMediumStore()
      if (this.selectedMediaIds.length === 1) {
        mediumStore.pullSoleFromMedia()
      } else {
        mediumStore.$reset()
      }
    },

    clearSelected() {
      this.selectedMediaIds = []
      useMediumStore().$reset()
    },

    async create({ file, cutline, credit }) {
      let formData = new FormData()

      formData.append(`medium[upload]`, file)
      // Include cutline/credit if provided by user, otherwise backend will extract from EXIF
      if (cutline) formData.append(`medium[cutline]`, cutline)
      if (credit) formData.append(`medium[credit]`, credit)

      return httpClient.post('/api/internal/media', formData)
        .then(({ data: { medium } }) => {
          this.media.unshift(medium) // Since it's the newest, it goes at the beginning
          this.media.pop() // Remove the last medium, so we keep the same page length
          return medium
        })
        .catch((err) => {
          useMessagesStore().smartAdd(err)
          throw err
        })
    },

    async destroy({ ids }) {
      this.loading = true

      return httpClient.delete(
        '/api/internal/media/batch_destroy',
        { params: { ids } }
      ).then(() => {
        this.clearSelected()
        this.search() // search will set loading to false for us
      }).catch(err => {
        this.loading = false
        console.error(err)
        useMessagesStore().addError(err)
      })
    }
  }
})
